import React, { useState } from 'react';
import {
  Box,
  VStack,
  Card,
  CardHeader,
  CardBody,
  Text,
  SimpleGrid,
  useColorModeValue,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useDisclosure,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { LineChart, PieChart, BarChart } from '../components/ChartCard';
import { useAuth } from '../context/AuthContext';
import UserDashboardModal from '../components/UserDetailModal';

const MotionCard = motion(Card);

// Dummy data for statistics
const statisticsData = {
  userStats: {
    totalUsers: 150,
    activeUsers: 120,
    newUsers: 30,
    userGrowth: [
      { month: 'Jan', users: 100 },
      { month: 'Feb', users: 120 },
      { month: 'Mar', users: 150 },
      { month: 'Apr', users: 180 },
      { month: 'May', users: 200 },
    ],
  },
  ticketStats: {
    totalTickets: 500,
    resolvedTickets: 450,
    openTickets: 50,
    ticketsByStatus: [
      { status: 'Open', count: 50 },
      { status: 'In Progress', count: 100 },
      { status: 'Resolved', count: 350 },
    ],
  },
  performanceStats: {
    averageResponseTime: '2.5 hours',
    satisfactionRate: '95%',
    performanceByAgent: [
      { agent: 'Agent 1', performance: 95 },
      { agent: 'Agent 2', performance: 88 },
      { agent: 'Agent 3', performance: 92 },
    ],
  },
};

const Statistics = () => {
  const { currentUser, isSuperAdmin, isAdmin } = useAuth();
  const cardBg = useColorModeValue('white', 'gray.700');
  const [selectedUser, setSelectedUser] = useState(null);
  const { isOpen: isUserDashboardOpen, onOpen: onUserDashboardOpen, onClose: onUserDashboardClose } = useDisclosure();

  const handleUserClick = (user) => {
    setSelectedUser(user);
    onUserDashboardOpen();
  };

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.800')}>
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          <Heading size="xl" mb={4}>Statistics Dashboard</Heading>

          {/* Stats Cards */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Card bg={cardBg} boxShadow="lg">
              <CardBody>
                <Stat>
                  <StatLabel>Total Users</StatLabel>
                  <StatNumber>{statisticsData.userStats.totalUsers}</StatNumber>
                  <StatHelpText>
                    <StatArrow type="increase" />
                    23.36%
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={cardBg} boxShadow="lg">
              <CardBody>
                <Stat>
                  <StatLabel>Total Tickets</StatLabel>
                  <StatNumber>{statisticsData.ticketStats.totalTickets}</StatNumber>
                  <StatHelpText>
                    <StatArrow type="increase" />
                    12.5%
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={cardBg} boxShadow="lg">
              <CardBody>
                <Stat>
                  <StatLabel>Satisfaction Rate</StatLabel>
                  <StatNumber>{statisticsData.performanceStats.satisfactionRate}</StatNumber>
                  <StatHelpText>
                    <StatArrow type="increase" />
                    2.5%
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Charts Section */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Card bg={cardBg} boxShadow="lg">
              <CardHeader>
                <Heading size="md">User Growth</Heading>
              </CardHeader>
              <CardBody>
                <LineChart 
                  data={statisticsData.userStats.userGrowth}
                  xKey="month"
                  yKey="users"
                  title="User Growth Trend"
                />
              </CardBody>
            </Card>

            <Card bg={cardBg} boxShadow="lg">
              <CardHeader>
                <Heading size="md">Ticket Status</Heading>
              </CardHeader>
              <CardBody>
                <PieChart 
                  data={statisticsData.ticketStats.ticketsByStatus}
                  nameKey="status"
                  valueKey="count"
                  title="Ticket Status Distribution"
                />
              </CardBody>
            </Card>
          </SimpleGrid>

          <Card bg={cardBg} boxShadow="lg">
            <CardHeader>
              <Heading size="md">Agent Performance</Heading>
            </CardHeader>
            <CardBody>
              <BarChart 
                data={statisticsData.performanceStats.performanceByAgent}
                xKey="agent"
                yKey="performance"
                title="Agent Performance"
              />
            </CardBody>
          </Card>
        </VStack>
      </Box>

      {/* Add the UserDashboardModal */}
      <UserDashboardModal
        isOpen={isUserDashboardOpen}
        onClose={onUserDashboardClose}
        userData={selectedUser}
      />
    </Box>
  );
};

export default Statistics; 